import { lazy, Suspense, type ComponentType } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { config, local } from '@accounts/config';
import { Confirmation, Resume } from '@accounts/domain';
import { Inbox, Place, Screen, Shell } from '@accounts/navigation';
import { usePresence } from '@accounts/session';
import { Callout, Loading } from '@beyond-js/ui/react';
import { Go, Heading, Outage, Wordmark } from '@accounts/ui';
import { Account } from './pages/account';
import { Confirm } from './pages/confirm';
import { Connect } from './pages/connect';
import { ConnectedApps } from './pages/connections';
import { Consent } from './pages/consent';
import { InvitationAcceptance } from './pages/invitation';
import { Organization } from './pages/organization';
import { OrganizationList } from './pages/organizations';
import { Overview } from './pages/overview';
import { Reset } from './pages/reset';
import { Resumed } from './pages/resumed';
import { Security } from './pages/security';
import { SignIn } from './pages/signin';
import { Factor } from './pages/signin/factor';
import { SignUp } from './pages/signup';
import { Activity } from './pages/activity';
import { Received } from './pages/received';
import { Tokens } from './pages/tokens';
import { TokenCreation } from './pages/tokens/creation';
import { Leave } from './pages/local/leave';
import { Landed } from './pages/landed';

/**
 * Local development only: the quick login and the person switch it leads to. They are loaded only by a
 * build that names the local people helper; in any other build `local` is the literal `false`, these
 * are `null`, and the bundler leaves both pages and their helper client out of the output.
 */
const QuickLogin = local ? lazy(() => import('./pages/local/quick').then(module => ({ default: module.QuickLogin }))) : null;
const Switch = local ? lazy(() => import('./pages/local/switch').then(module => ({ default: module.Switch }))) : null;

/** The local routes, in either standing; nothing at all without the helper */
function locals(loading: string) {
	// Tested first on its own: a literal `false` makes the rest unreachable, and the bundler drops it
	if (!local) return null;
	// A local page's code is still the document's opening when the page is the first one loaded: the
	// opening waits for it and says it in this line's own words
	const wait = (
		<div data-bui-opening-wait="">
			<Loading label={loading} />
		</div>
	);
	const page = (Page: ComponentType | null) => (Page ? <Suspense fallback={wait}><Page /></Suspense> : null);
	return [<Route key="quick" path="/dev/quicklogin" element={page(QuickLogin)} />, <Route key="switch" path="/switch" element={page(Switch)} />];
}

/**
 * What is shown for each standing.
 *
 * The four standings are kept apart on purpose. "Still reading" is not "signed out", and an
 * unreachable service is neither: showing a sign-in form when Accounts is down invites a person to
 * type a password into a screen that cannot use it, and tells them something untrue about their
 * session.
 */
export function App() {
	const { standing, text, refresh, language, refusal } = usePresence();
	const location = useLocation();

	// The session window's landing, whoever is signed in and whether or not Accounts answers
	if (location.pathname === Resume.LANDING) return <Landed />;

	if (standing === 'reading') {
		// The document's opening stays until a screen replaces this, and says this step in the family's
		// words ("Confirming your session", `@beyond-js/ui` 0.21.0)
		return (
			<div data-bui-opening-wait="session" className="mx-auto max-w-md px-4 py-16">
				<Loading label={Loading.labels[language]} />
			</div>
		);
	}

	if (standing === 'unavailable') {
		return (
			<div className="mx-auto flex max-w-md flex-col gap-4 px-4 py-16">
				{/* The same mark as every signed-out screen, so a person knows where they are while it is down */}
				<Wordmark label="Accounts" />
				{/* Where the person is, as the place's H1, with the outage's own heading under it */}
				<Heading title={text(Place.key(location.pathname))} />
				{/* Nothing could be read, so the outage is the screen (D163), read again by itself until Accounts answers */}
				<Outage whole reads={[{ key: 'session', title: text('outage.session'), unread: refusal, outage: true, run: refresh }]} />
			</div>
		);
	}

	if (standing === 'out') {
		return (
			<Routes>
				<Route path="/invitations/:secret" element={<InvitationAcceptance />} />
				{/* Both are reached from a message, by someone who has no session yet: that is the
				    whole point of them */}
				<Route path="/reset" element={<Reset />} />
				<Route path="/confirm" element={<Confirm />} />
				<Route path="/signin" element={<SignIn />} />
				<Route path="/signin/factor" element={<Factor />} />
				<Route path="/signup" element={<SignUp />} />
				{locals(Loading.labels[language])}
				<Route path="/leave" element={<Leave />} />
				{/* An application that could not even ask is said to anyone; a request to decide goes to sign in */}
				<Route path="/connect" element={<Connect />} />
				{/* The destination is kept with its query, so signing in returns the person where they were
				    going — including a product's request to be returned to, which is validated there */}
				<Route path="*" element={<Navigate to={`/signin?to=${encodeURIComponent(`${location.pathname}${location.search}`)}`} replace />} />
			</Routes>
		);
	}

	// An invitation is reachable in either standing, so it is routed before anything else in both
	const hashed = new URLSearchParams(location.search).has('token_hash');
	return (
		<Routes>
			<Route path="/invitations/:secret" element={<InvitationAcceptance />} />
			{locals(Loading.labels[language])}
			<Route path="/leave" element={<Leave />} />
			{/* An installed client's hand-off waits here for the person, before the frame like signing in */}
			<Route path="/consent" element={<Consent />} />
			{/* So does an application's request to use a product for the person (OAuth) */}
			<Route path="/connect" element={<Connect />} />
			{/* A message's link is honoured signed in too: a new address is confirmed, a password reset; an
			    older link that signed in already goes on to where the sign-in was going */}
			<Route path="/reset" element={hashed ? <Reset /> : <Navigate to="/security" replace />} />
			<Route path="/confirm" element={hashed ? <Confirm /> : <Navigate to={new Confirmation(new URLSearchParams(location.search), config.authority).signin} replace />} />
			<Route element={<Shell />}>
				<Route index element={<Overview />} />
				<Route path="organizations" element={<OrganizationList />} />
				<Route path="organizations/:organization" element={<Organization />} />
				<Route path="account" element={<Account />} />
				<Route path="security" element={<Security />} />
				<Route path="notifications" element={<Inbox />} />
				<Route path="activity" element={<Activity />} />
				<Route path="invitations" element={<Received />} />
				<Route path="tokens" element={<Tokens />} />
				<Route path="tokens/new" element={<TokenCreation />} />
				<Route path="connections" element={<ConnectedApps />} />
				<Route path="signin" element={<Resumed />} />
				<Route
					path="*"
					element={
						<Screen>
							<Callout tone="warning" live title={text('state.notfound')} actions={<Go to="/" label={text('notfound.action')} />} />
						</Screen>
					}
				/>
			</Route>
		</Routes>
	);
}

